import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { firebaseService } from '../services/firebaseService';
import { SistemaNotificacao } from '../types/Notification';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  notificationCount: number;
  onCountChange?: (count: number) => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ 
  isOpen, 
  onClose, 
  notificationCount,
  onCountChange 
}) => {
  const { userProfile } = useAuth();
  const [notifications, setNotifications] = useState<SistemaNotificacao[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && userProfile?.empresaCnpj && userProfile?.id) {
      loadNotifications();
    }
  }, [isOpen, userProfile?.empresaCnpj, userProfile?.id]);

  const loadNotifications = async () => {
    if (!userProfile?.empresaCnpj || !userProfile?.id) return;

    try {
      setLoading(true);
      const notificacoes = await firebaseService.getNotificacoesUsuario(
        userProfile.empresaCnpj,
        userProfile.id
      );
      setNotifications(notificacoes);
      
      // Atualizar contador
      const unreadCount = notificacoes.filter(n => !n.lida).length;
      if (onCountChange) {
        onCountChange(unreadCount);
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fechar quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const markAsRead = async (id: string) => {
    if (!userProfile?.empresaCnpj || !userProfile?.id) return;

    try {
      await firebaseService.marcarNotificacaoComoLida(
        userProfile.empresaCnpj,
        userProfile.id,
        id
      );
      
      // Atualizar estado local
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === id ? { ...notif, lida: true, dataLeitura: new Date().toISOString() } : notif
        )
      );

      // Atualizar contador
      const unreadCount = notifications.filter(n => !n.lida && n.id !== id).length;
      if (onCountChange) {
        onCountChange(unreadCount);
      }
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!userProfile?.empresaCnpj || !userProfile?.id) return;

    try {
      await firebaseService.marcarTodasComoLidas(
        userProfile.empresaCnpj,
        userProfile.id
      );
      
      // Atualizar estado local
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, lida: true, dataLeitura: new Date().toISOString() }))
      );

      // Atualizar contador
      if (onCountChange) {
        onCountChange(0);
      }
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.lida).length;

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 z-50 mt-2 w-80 origin-top-right rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black dark:ring-gray-700 ring-opacity-5 focus:outline-none"
    >
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notificações</h3>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              Marcar todas como lidas
            </button>
          )}
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400 mx-auto mb-2"></div>
            <p className="text-sm">Carregando notificações...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p className="text-sm">Nenhuma notificação</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
                  !notification.lida ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
                onClick={() => {
                  if (notification.link) {
                    window.location.href = notification.link;
                  }
                  if (!notification.lida) {
                    markAsRead(notification.id);
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 mt-1 ${
                    notification.tipo === 'success' ? 'text-success-600' :
                    notification.tipo === 'warning' ? 'text-warning-600' :
                    notification.tipo === 'error' ? 'text-danger-600' :
                    'text-primary-600'
                  }`}>
                    <div className={`h-2 w-2 rounded-full ${
                      !notification.lida ? 'bg-primary-600 dark:bg-primary-400' : 'bg-gray-300 dark:bg-gray-600'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${
                      !notification.lida ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {notification.titulo}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {notification.mensagem}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {new Date(notification.dataCriacao).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  {!notification.lida && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notification.id);
                      }}
                      className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Marcar como lida"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {notifications.length > 0 && (
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 text-center">
          <button
            onClick={onClose}
            className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            Ver todas as notificações
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;

